import React from 'react';
import { motion } from 'framer-motion';
import { 
  Zap, 
  Landmark, 
  Leaf, 
  ShieldCheck, 
  Sun, 
  CheckCircle2, 
  ArrowUpRight 
} from 'lucide-react';

import billsImg from '../assets/lower electricity.png';
import subsidyImg from '../assets/govt subsidies.png';
import ecoImg from '../assets/eco freindly.png';
import safeguardImg from '../assets/multi.png';

const WhyChooseSolar = () => {
  const benefits = [
    {
      id: '01',
      tag: 'Up to 85% Savings',
      title: 'Lower Electricity Bills',
      description: 'Shield your finances against rising utility tariffs. Cut down your daytime power bills immediately with intelligent solar arrays.',
      image: billsImg,
      icon: Zap,
      highlight: 'Instant Monthly Relief',
    },
    {
      id: '02',
      tag: 'Govt Subsidies',
      title: 'Subsidies & Tax Benefits',
      description: 'Maximize your ROI by claiming central and state rooftop subsidies. We handle the paperwork and liaisoning from start to finish.',
      image: subsidyImg,
      icon: Landmark,
      highlight: 'End-to-End Liaisoning',
    },
    {
      id: '03',
      tag: 'Zero Carbon',
      title: 'Eco-Friendly Solution',
      description: 'Mitigate tons of greenhouse emissions every year. Transition your home or enterprise into a self-sustaining clean energy powerhouse.',
      image: ecoImg,
      icon: Leaf,
      highlight: '100% Clean Energy',
    },
    {
      id: '04',
      tag: '25-Year Cover',
      title: 'Multi-Decade Safeguard',
      description: 'Engineered for extreme weather with Tier-1 bifacial panels, certified rust-proof structures, and performance guarantees.',
      image: safeguardImg,
      icon: ShieldCheck,
      highlight: 'Guaranteed Performance',
    },
  ];

  return (
    <section id="benefits" className="py-12 sm:py-16 lg:py-20 bg-slate-50 relative overflow-hidden border-b border-slate-200/60 font-sans">
      
      {/* 🌟 AMBIENT BACKGROUND GLOW & PATTERN 🌟 */}
      <div className="absolute top-0 right-1/4 w-72 h-72 sm:w-96 sm:h-96 bg-[#F7941D]/5 rounded-full blur-[80px] sm:blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-72 h-72 sm:w-96 sm:h-96 bg-[#1E5CB3]/5 rounded-full blur-[80px] sm:blur-[120px] pointer-events-none" />
      
      {/* Subtle Blue Dot Grid for Tech Feel */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none" 
        style={{ 
          backgroundImage: `radial-gradient(#0D3F7F 1.5px, transparent 1.5px)`,
          backgroundSize: '24px 24px' 
        }} 
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* 🌟 1. SECTION HEADER 🌟 */}
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-16">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-[#F0F7FF] border border-[#1E5CB3]/20 px-3.5 py-1 sm:px-4 sm:py-1.5 rounded-full mb-3 sm:mb-4 shadow-sm"
          >
            <Sun className="w-3.5 h-3.5 text-[#F7941D]" />
            <span className="text-[9px] sm:text-[11px] font-bold tracking-[0.12em] sm:tracking-[0.15em] text-[#0D3F7F] uppercase">
              Infrastructural Superiority
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-[#0F172A] tracking-tight leading-tight"
          >
            Why Choose <span className="bg-gradient-to-r from-[#F7941D] to-[#FFC107] bg-clip-text text-transparent drop-shadow-sm">Dev Deepam Solar?</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="text-slate-500 text-xs sm:text-sm md:text-[15px] font-medium mt-3 sm:mt-4 leading-relaxed max-w-2xl mx-auto px-1 sm:px-0"
          >
            We bridge precision German-grade engineering with seamless government approvals to deliver solar infrastructure that lasts for generations.
          </motion.p>
        </div>

        {/* 🌟 2. CARDS GRID 🌟 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 lg:gap-7">
          {benefits.map((benefit, index) => {
            const IconComponent = benefit.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 35 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-20px" }}
                transition={{ delay: index * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -8 }}
                className="bg-white rounded-2xl overflow-hidden border border-slate-200/80 shadow-[0_4px_15px_rgba(0,0,0,0.02)] hover:shadow-[0_20px_40px_-15px_rgba(247,148,29,0.15)] hover:border-[#F7941D]/30 transition-all duration-400 group flex flex-col justify-between"
              >
                <div>
                  {/* Image Container with Floating Badges */}
                  <div className="aspect-[16/11] xs:aspect-[16/10] sm:aspect-[16/11] w-full overflow-hidden bg-slate-100 relative">
                    <img 
                      src={benefit.image} 
                      alt={benefit.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                    />
                    {/* Dark Gradient Overlay for text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent opacity-75 group-hover:opacity-50 transition-opacity" />

                    {/* Top Left Index Tag */}
                    <div className="absolute top-2.5 left-2.5 sm:top-3 sm:left-3 flex items-center gap-1 sm:gap-1.5 bg-white/95 backdrop-blur-md px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full border border-white/60 shadow-sm">
                      <span className="text-[9px] sm:text-[10px] font-black text-[#0D3F7F]">{benefit.id}</span>
                      <span className="w-1 h-1 rounded-full bg-[#F7941D]" />
                      <span className="text-[9px] sm:text-[10px] font-bold text-slate-700">{benefit.tag}</span>
                    </div>

                    {/* Top Right Tech Icon Badge */}
                    <div className="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-black/40 backdrop-blur-md border border-white/20 flex items-center justify-center text-white group-hover:bg-[#F7941D] group-hover:border-[#F7941D] group-hover:text-white transition-all duration-300 shadow-sm">
                      <IconComponent className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </div>

                    {/* Bottom Floating Highlight Pill */}
                    <div className="absolute bottom-2.5 left-2.5 right-2.5 sm:bottom-3 sm:left-3 sm:right-3">
                      <span className="inline-flex items-center gap-1.5 text-[9px] sm:text-[11px] font-semibold text-white/95 bg-black/40 backdrop-blur-md px-2.5 py-1 rounded-lg border border-white/20">
                        <CheckCircle2 className="w-3 h-3 text-[#F7941D] flex-shrink-0" />
                        <span className="truncate">{benefit.highlight}</span>
                      </span>
                    </div>
                  </div>

                  {/* Text Content */}
                  <div className="p-4 sm:p-5 lg:p-6">
                    <h3 className="text-15px sm:text-[16px] lg:text-[17px] font-bold text-[#0D3F7F] tracking-tight mb-2 group-hover:text-[#F7941D] transition-colors duration-300 flex items-center justify-between gap-1">
                      <span className="line-clamp-1">{benefit.title}</span>
                      <ArrowUpRight className="w-4 h-4 text-slate-400 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all group-hover:text-[#F7941D] flex-shrink-0" />
                    </h3>
                    <p className="text-slate-500 font-medium text-[12px] sm:text-[13px] leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>

                {/* Bottom Active Orange/Yellow Accent Line on Hover */}
                <div className="h-1 w-0 bg-gradient-to-r from-[#F7941D] to-[#FFC107] group-hover:w-full transition-all duration-500 ease-out" />
              </motion.div>
            );
          })}
        </div>

        {/* 🌟 3. BOTTOM TRUST STRIP (Premium Navy Blue) 🌟 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-10 sm:mt-16 p-4 sm:p-6 rounded-2xl bg-[#0D3F7F] border border-[#1E5CB3]/30 shadow-lg grid grid-cols-1 md:flex md:flex-wrap md:items-center md:justify-around gap-4 sm:gap-5 md:gap-6 text-xs sm:text-[13px] font-bold text-white text-center"
        >
          <div className="flex items-center justify-center gap-2.5 group">
            <CheckCircle2 className="w-4 h-4 text-[#FFC107] flex-shrink-0 group-hover:scale-110 transition-transform" />
            <span className="tracking-wide">MNRE Registered Channel Partner</span>
          </div>
          <div className="hidden md:block w-1.5 h-1.5 rounded-full bg-[#1E5CB3]" />
          <div className="flex items-center justify-center gap-2.5 group">
            <CheckCircle2 className="w-4 h-4 text-[#FFC107] flex-shrink-0 group-hover:scale-110 transition-transform" />
            <span className="tracking-wide">Tier-1 Monocrystalline Modules</span>
          </div>
          <div className="hidden md:block w-1.5 h-1.5 rounded-full bg-[#1E5CB3]" />
          <div className="flex items-center justify-center gap-2.5 group">
            <CheckCircle2 className="w-4 h-4 text-[#FFC107] flex-shrink-0 group-hover:scale-110 transition-transform" />
            <span className="tracking-wide">100% Direct Bank Subsidy Assistance</span>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default WhyChooseSolar;